// src/Terms.js
import React from "react";

export default function Terms() {
  return (
    <div style={{ maxWidth: 900, margin: "60px auto", padding: "0 20px" }}>
      <h1>Terms &amp; Conditions</h1>

      <p>
        Welcome to <strong>hellastay</strong>. These Terms &amp; Conditions (“Terms”)
        govern your access to and use of the hellastay website and services (the “Platform”).
        By creating an account or using the Platform, you agree to these Terms.
      </p>

      <p style={{ color: "rgba(0,0,0,0.65)", fontSize: 13, marginTop: 10 }}>
        Last updated: <strong>2026</strong>
      </p>

      <h2>1. The Platform</h2>
      <p>
        <strong>hellastay</strong> is a marketplace that connects guests and hosts.
        We provide tools to list properties, request bookings, communicate, and facilitate payments.
        Unless explicitly stated otherwise, <strong>hellastay is not the owner or operator of listed properties</strong>.
      </p>

      <h2>2. Eligibility &amp; Accounts</h2>
      <ul>
        <li>You must provide accurate information and keep it up to date.</li>
        <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
        <li>You must not use the Platform for unlawful purposes or in violation of these Terms.</li>
      </ul>

      <h2>3. Hosts</h2>
      <ul>
        <li>
          Hosts are responsible for listing accuracy (location, photos, amenities, house rules, price,
          availability), and for complying with local laws and tax obligations.
        </li>
        <li>
          Hosts must provide a safe and lawful stay and communicate clearly with guests.
        </li>
        <li>
          Hosts must not discriminate unlawfully and must follow fair and respectful conduct.
        </li>
      </ul>

      <h2>4. Guests</h2>
      <ul>
        <li>
          Guests must provide accurate booking details and respect the property, house rules, neighbors,
          and applicable laws.
        </li>
        <li>
          Guests are responsible for damages caused by them or members of their party, to the extent allowed by law.
        </li>
      </ul>

      <h2>5. Booking Requests &amp; Confirmations</h2>
      <p>
        A booking may be confirmed once the host accepts (where applicable) and payment is successfully authorized.
        Booking details (dates, guest count, price, rules) are binding once confirmed.
      </p>

      <h2>6. Payments, Fees &amp; Escrow-Style Payouts</h2>
      <p>
        Payments may be processed by a third-party payment provider (e.g., Stripe).
        <strong> hellastay does not store your full card details</strong>.
        We may receive transaction references and status information to operate the service.
      </p>

      <p>
        If an escrow-style flow is enabled, guest funds may be held temporarily and released to the host
        after check-in confirmation and/or after a short grace period, unless an issue is reported.
      </p>

      <ul>
        <li>
          <strong>Check-in confirmation</strong>: a 6-digit check-in code may be used to confirm arrival.
        </li>
        <li>
          <strong>Grace period</strong>: payouts may be released automatically after a short period (e.g., ~24 hours).
          For new hosts, a longer grace period may apply (e.g., 48–72 hours) as an additional safety measure.
        </li>
        <li>
          <strong>Problems &amp; disputes</strong>: if a guest reports a problem within the applicable time window,
          payout may be paused while the issue is reviewed.
        </li>
      </ul>

      <p>
        Any service fees (if/when applicable) will be clearly displayed before you complete a booking or publish a listing.
      </p>

      <h2>7. Cancellations &amp; Refunds</h2>
      <p>
        Cancellation and refund rules may depend on the host’s chosen policy, local law, and the booking context.
        When available, the applicable cancellation policy will be displayed during booking.
      </p>

      <p>
        If an escrow-style flow applies and a valid issue is reported on time, hellastay may facilitate communication
        and may pause payouts while reviewing evidence from both sides.
      </p>

      <h2>8. Reviews, Content &amp; Conduct</h2>
      <ul>
        <li>
          Users may post content (e.g., listings, photos, descriptions, reviews) and are responsible for it.
        </li>
        <li>
          You must not post illegal, misleading, infringing, hateful, or abusive content.
        </li>
        <li>
          We may remove content or suspend accounts that violate these Terms or harm Platform integrity.
        </li>
      </ul>

      <h2>9. Prohibited Activities</h2>
      <ul>
        <li>Fraud, impersonation, misleading listings or booking information.</li>
        <li>Unauthorized scraping, reverse engineering, or attempts to bypass security.</li>
        <li>Harassment, abuse, or threatening behavior.</li>
        <li>Using the Platform to violate laws or third-party rights.</li>
      </ul>

      <h2>10. Availability &amp; Changes</h2>
      <p>
        We work to keep the Platform available, but it may sometimes be interrupted for maintenance,
        updates, or technical issues. We may update features over time.
      </p>

      <h2>11. Disclaimers</h2>
      <p>
        The Platform is provided on an “as is” basis, to the extent permitted by law.
        hellastay does not guarantee the quality, safety, or legality of listings or stays,
        and does not become a party to the agreement between guest and host.
      </p>

      <h2>12. Limitation of Liability</h2>
      <p>
        To the extent permitted by law, hellastay will not be liable for indirect, incidental,
        special, consequential, or punitive damages, or for loss of profits, revenues, or data,
        arising from your use of the Platform.
      </p>

      <h2>13. Termination</h2>
      <p>
        You may stop using the Platform at any time. We may suspend or terminate access
        if you violate these Terms, misuse the Platform, or create risk for other users or the Platform.
      </p>

      <h2>14. Charity Pledge (Donation Commitment)</h2>
      <p>
        After the Platform is monetized, <strong>hellastay</strong> intends to donate a percentage of net profit
        to charitable causes. Details (percentage, beneficiaries, reporting) will be publicly shared once the
        monetization model is finalized and launched.
      </p>

      <h2>15. Privacy</h2>
      <p>
        Our collection and use of personal data is described in our{" "}
        <a href="/privacy" style={{ color: "var(--brandDark)", fontWeight: 600 }}>
          Privacy Policy
        </a>
        .
      </p>

      <h2>16. Contact</h2>
      <p>
        Email: <strong>hellasstay@gmail.com</strong>
      </p>
    </div>
  );
}